// ** React Imports
import { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getRankings } from "store/modules/main-pages/rankings/get-ranking.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import SelectorSelectMulti from "components/layout/DropdownSelectors/SelectorSelectMulti";

// ** Custom Hooks
import useFilterSex, {
	compute as filterSexCompute,
} from "hooks/filters/useFilterSex";
import { compute as filterPeriodCompute } from "hooks/filters/useFilterPeriod";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import { ANY, RANKING_PERIOD } from "helpers/constants";
import { searchSexCustomSort } from "utils/searchSexCustomSort";

// #####################################################

const useRankingSexSelector = () => {
	const { t } = useTranslation("common");
	const { push } = useHistory();

	const dispatch = useDispatch();

	const params = useParams();
	const {
		country: countryUrl,
		region: regionUrl,
		sex: sexUrl,
		period: periodUrl,
	} = params || {};

	const userSearchSex = useSelector((state) => state.global.user.searchSex);
	const userSearchUrl = useSelector((state) => state.global.user.urlSearch);
	const userCountryFromSearchUrl = userSearchUrl.split("/")[0];

	const initialFilterSex = useMemo(() => {
		if (sexUrl) {
			return sexUrl;
		} else if (
			userSearchSex &&
			Array.isArray(userSearchSex) &&
			userSearchSex.length > 0
		) {
			return [...userSearchSex].sort(searchSexCustomSort);
		}
		return "";
		// return "woman-man-couple-transsexual";
	}, [sexUrl, userSearchSex]);

	const filterSex = useFilterSex({
		initial: initialFilterSex,
		multiple: true,
	});

	const { items, selected, compute, isSelected } = filterSex;

	const renderMultipleSexSelector = (setFiltersOpen) => (
		<SelectorSelectMulti
			label={t("gender")}
			emptyText={t("common:any.2")}
			items={items}
			selected={selected}
			onAccept={(value) => {
				const to = generatePath(
					"/rankings/:rankingPeriod/:sex/:country/:region",
					{
						rankingPeriod: periodUrl
							? periodUrl
							: RANKING_PERIOD.TODAY,
						country: countryUrl
							? countryUrl
							: userCountryFromSearchUrl || ANY,
						region: regionUrl || ANY,
						sex: compute.valueToUrl(value) || ANY,
					}
				);
				dispatch(
					getRankings({
						apiParams: {
							period: filterPeriodCompute.urlToApiParam(
								periodUrl ? periodUrl : RANKING_PERIOD.TODAY
							),
							sex:
								filterSexCompute(true).valueToApiParam(value) ||
								ANY,
							country: countryUrl
								? countryUrl
								: userCountryFromSearchUrl || ANY,
							region: regionUrl || ANY,
						},
						useUrl: true,
					})
				);
				push(to);
				if (typeof setFiltersOpen === "function") {
					setFiltersOpen(false);
				}
			}}
			withMobileVersion
		/>
	);

	// #####################################################

	return {
		renderMultipleSexSelector,
		isSelected,
	};
};

export default useRankingSexSelector;

// #####################################################
